import React from 'react';
import { Document, Text, StyleSheet } from '@react-pdf/renderer';
import type { Grade } from '../../../types';
import type { ExamGradesheet, GradesheetStudentRow, GradesheetSubjectCell } from '../../../services/studentReportsService';
import {
  Page, DataTable, fitHeaderStyle, pdfColors, pdfSpacing, fmtClassName, fmtDate,
  type DataTableColumn, type SchoolBranding,
} from '../../../utils/pdf';
import GradeSummary from './GradeSummary';
import { MARK_GREEN, MARK_RED, PctPill, gradeColorOf } from './scoreMarks';

interface Props {
  data: ExamGradesheet;
  grades: Grade[];
  branding: SchoolBranding;
  generatedAt: string;
}

// A4 landscape inside the page frame.
const CONTENT_W = 762;
const STUDENT_MIN_W = 120;

const OUTCOME: Record<string, { label: string; color: string }> = {
  PASS: { label: 'Pass', color: MARK_GREEN },
  FAIL: { label: 'Fail', color: MARK_RED },
  PENDING: { label: 'In progress', color: pdfColors.textMuted },
};

const styles = StyleSheet.create({
  strong: { fontWeight: 600, color: pdfColors.textPrimary },
  muted: { color: pdfColors.textMuted },
  faint: { color: pdfColors.textFaint },
  absent: { color: MARK_RED, fontWeight: 700 },
  pending: { color: '#d97706', fontWeight: 600 },
  failed: { color: MARK_RED, fontWeight: 700 },
  empty: {
    marginTop: pdfSpacing.lg, paddingVertical: 36, textAlign: 'center', fontSize: 9, color: pdfColors.textMuted, fontStyle: 'italic',
    borderWidth: 0.6, borderColor: pdfColors.dividerStrong, borderStyle: 'dashed', borderRadius: 4,
  },
});

/** A subject's mark as the grid shows it: the marks, AB for absent, a dash when pending or not taken. */
const Mark: React.FC<{ c: GradesheetSubjectCell; failed: boolean }> = ({ c, failed }) => {
  if (c.status === 'ABSENT') return <Text style={styles.absent}>AB</Text>;
  if (c.status === 'PENDING') return <Text style={styles.pending}>—</Text>;
  if (c.status === 'NOT_TAKEN') return <Text style={styles.faint}>—</Text>;
  return <Text style={failed ? styles.failed : undefined}>{String(c.obtainedRaw ?? c.display)}</Text>;
};

/**
 * An exam's gradesheet for a class or section: the school's letterhead, the
 * class average and how the grades fell, then every student ranked — marks
 * per subject (a failed paper in red), the total, the percentage in its
 * grade's colour, the grade and, when the school has pass rules, the result.
 */
const GradesheetDocument: React.FC<Props> = ({ data, grades, branding, generatedAt }) => {
  const gradeColor = gradeColorOf(grades);
  const cls = fmtClassName(data.class.name);
  const scope = data.section ? `${cls} · Section ${data.section.name}` : cls;
  const dates = data.exam.startDate && data.exam.endDate ? `${fmtDate(data.exam.startDate)} – ${fmtDate(data.exam.endDate)}` : null;
  const passRules = data.stats.passRules;

  // "/max" goes in the Total heading when every student is out of the same total; otherwise on each total.
  const uniformMax = data.students.length > 0 && data.students.every(s => s.totalMax === data.students[0].totalMax)
    ? data.students[0].totalMax : null;

  const fixed = 30 + 32 + (data.section ? 0 : 46) + 56 + 52 + 40 + (passRules ? 54 : 0);
  const subjectW = Math.max(30, Math.min(64, (CONTENT_W - fixed - STUDENT_MIN_W) / Math.max(data.subjects.length, 1)));
  const subjectHeader = fitHeaderStyle(data.subjects.map(s => s.name), subjectW);

  const columns: Array<DataTableColumn<GradesheetStudentRow>> = [
    { key: 'rank', header: 'Rank', width: 30, align: 'center', render: s => <Text style={styles.strong}>{s.rank ?? '—'}</Text> },
    { key: 'roll', header: 'Roll', width: 32, align: 'center', render: s => String(s.rollNumber) },
    { key: 'name', header: 'Student', flex: 1, render: s => <Text style={styles.strong}>{s.name}</Text> },
    ...(data.section ? [] : [{ key: 'section', header: 'Section', width: 46, align: 'center' as const, render: (s: GradesheetStudentRow) => s.sectionName || '—' }]),
    ...data.subjects.map((sub, k): DataTableColumn<GradesheetStudentRow> => ({
      key: `s${k}`, header: `${sub.name}\n/${sub.maxMarks}`, width: subjectW, align: 'center', headerStyle: subjectHeader,
      render: s => <Mark c={s.perSubject[k]} failed={s.failedSubjectIds.includes(sub.subjectId)} />,
    })),
    {
      key: 'total', header: uniformMax ? `Total\n/${uniformMax}` : 'Total', width: 56, align: 'center',
      render: s => <Text style={styles.strong}>{s.totalMax <= 0 ? '—' : uniformMax ? String(s.totalObtained) : `${s.totalObtained}/${s.totalMax}`}</Text>,
    },
    { key: 'pct', header: '%', width: 52, align: 'center', render: s => <PctPill pct={s.totalMax > 0 ? s.overallPct : null} grades={grades} /> },
    {
      key: 'grade', header: 'Grade', width: 40, align: 'center',
      render: s => <Text style={{ fontWeight: 700, color: s.overallGrade ? gradeColor(s.overallPct) : pdfColors.textFaint }}>{s.overallGrade || '—'}</Text>,
    },
    ...(passRules ? [{
      key: 'result', header: 'Result', width: 54, align: 'center' as const,
      render: (s: GradesheetStudentRow) => (s.outcome
        ? <Text style={{ fontWeight: 700, color: OUTCOME[s.outcome].color }}>{OUTCOME[s.outcome].label}</Text>
        : <Text style={styles.faint}>—</Text>),
    }] : []),
  ];

  return (
    <Document title={`Exam Gradesheet — ${data.exam.name} — ${scope}`} author={branding.name}>
      <Page
        branding={branding}
        orientation="landscape"
        meta={{ title: `Exam Gradesheet — ${data.exam.name}`, subtitle: `${data.session.name}  ·  ${scope}`, generatedAt }}
        official={{ details: [
          { label: 'Session', value: data.session.name },
          { label: 'Class', value: cls },
          ...(data.section ? [{ label: 'Section', value: data.section.name }] : []),
          { label: 'Exam', value: data.exam.name },
          ...(dates ? [{ label: 'Dates', value: dates }] : []),
        ], perRow: 5 }}
      >
        {data.students.length === 0 ? (
          <Text style={styles.empty}>No students are enrolled in this class for the exam.</Text>
        ) : (
          <>
            <GradeSummary
              average={data.stats.classAverage}
              averageColor={gradeColor(data.stats.classAverage)}
              tiles={data.stats.gradeDistribution.map(b => ({ label: b.grade, value: b.count, color: b.color }))}
            />
            <DataTable columns={columns} rows={data.students} compact small />
          </>
        )}
      </Page>
    </Document>
  );
};

export default GradesheetDocument;
